import React, { useEffect, useState } from 'react';
import { Dashboard } from './pages/Dashboard';
import { Statistics } from './pages/Statistics';
import { QuitPlan } from './pages/QuitPlan';
import { Onboarding } from './pages/Onboarding';
import { AcceptInvite } from './pages/AcceptInvite';
import { MoneySaved } from './pages/MoneySaved';
import { Explore } from './pages/Explore';
import { useStore } from './store/useStore';
import { useLiff } from './hooks/useLiff';

const App: React.FC = () => {
  const { activeTab, isInitialized, isAuthenticated, user } = useStore();
  const [inviteCode, setInviteCode] = useState<string | null>(null);

  // Initialize LIFF — this will auto-redirect to LINE Login if not logged in
  useLiff();

  // Read URL params (tab or invite code)
  // LIFF encodes extra query params inside "liff.state", so we parse both
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // Parse liff.state if present (LIFF URL: liff.line.me/ID/?tab=x → liff.state=%3Ftab%3Dx)
    const liffState = params.get('liff.state');
    const liffParams = liffState
      ? new URLSearchParams(liffState.startsWith('?') ? liffState.substring(1) : liffState)
      : null;

    const invite = params.get('invite') || liffParams?.get('invite');
    if (invite) {
      setInviteCode(invite);
      return;
    }

    const tab = params.get('tab') || liffParams?.get('tab');
    if (tab === 'profile') {
      // Profile is now inside SideMenu — go to home and auto-open menu
      useStore.setState({ activeTab: 'home', openSideMenu: true });
    } else if (tab === 'stats' || tab === 'home' || tab === 'community' || tab === 'money') {
      useStore.setState({ activeTab: tab });
    }
    // Set initial history state
    window.history.replaceState({ tab: tab || 'home' }, '', window.location.href);
  }, []);

  // Listen for browser back/forward button
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const tab = event.state?.tab || new URLSearchParams(window.location.search).get('tab') || 'home';
      useStore.setState({ activeTab: tab as any });
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // UI Component for elegant loading state
  const PremiumLoader = ({ text }: { text: string }) => (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      background: '#ffffff',
    }}>
      <div style={{
        position: 'relative', width: 200, height: 200, marginBottom: 24,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'bounce-soft 2.5s ease-in-out infinite',
        filter: 'drop-shadow(0px 10px 20px rgba(0,0,0,0.15))'
      }}>
        <img src="/Nong.png" alt="น้องปอด" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
      </div>
      <div style={{ color: '#111827', fontSize: 16, fontWeight: 800, letterSpacing: '0.2px', marginBottom: 6 }}>{text}</div>
      <div style={{ color: '#9ca3af', fontSize: 13, fontWeight: 500 }}>คุณกำลังเข้าสู่แอป ปอดดี</div>
    </div>
  );

  // 1) Loading — LIFF is initializing
  if (!isInitialized) {
    return <PremiumLoader text="กำลังเชื่อมต่อ..." />;
  }

  // 2) Not authenticated — waiting for LINE Login redirect
  if (!isAuthenticated) {
    return <PremiumLoader text="กำลังเข้าสู่ระบบ..." />;
  }

  // 3) Invite acceptance (after auth)
  if (inviteCode) {
    return (
      <AcceptInvite
        code={inviteCode}
        onDone={() => {
          setInviteCode(null);
          window.history.replaceState({}, '', window.location.pathname);
        }}
      />
    );
  }

  // 4) Viewer → skip onboarding, go straight to Dashboard
  // (Dashboard already handles viewer mode: shows owner data, read-only)
  const isViewer = (user as any)?.role === 'viewer' && (user as any)?.ownerUserId;

  // 5) New user (non-viewer) — show Onboarding first
  if (!isViewer && user && !user.onboardingCompleted) {
    return <Onboarding />;
  }

  // 6) Main app
  const renderContent = () => {
    switch (activeTab) {
      case 'home':
        return <Dashboard />;
      case 'stats':
        return <Statistics />;
      case 'community':
        return <QuitPlan />;
      case 'profile':
        return <Dashboard />;
      case 'money':
        return <MoneySaved />;
      case 'explore':
        return <Explore />;
      default:
        return <Dashboard />;
    }
  };

  return (
    <div className="app-wrapper">
      <div className="app-container">
        <div className="app-content">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default App;
